import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Input,
  Popover,
  Space,
  Table,
  TableProps,
  Tag,
  Tooltip,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_API_URL } from "../../../utils/consts";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../../utils/useAuth";
import { useTranslation } from "react-i18next";
import { isAdmin, isModerator } from "../../../utils/permissions";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DownOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  MoreOutlined,
  RightOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  UpOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { format } from "date-fns";
import { Modal } from "antd";
import { Typography } from "antd";
import { Tabs } from "antd";
import type { TabsProps } from "antd";
import { Select } from "antd";
import RevenueTypesModal from "../../../Components/Modals/RevenueTypesModal/RevenueTypesModal";
import { getUserWorkLocationsHelper } from "../../../utils/helpers/workLocationsHelper";
import "./DailyReventuSettings.scss";
const { Text } = Typography;
const { confirm } = Modal;

// Revenue types come pre-sorted by sortOrder from the server — no client-side reordering needed
export const applyRevenueTypeOrder = (types: any[]): any[] => types || [];

const SortableRevenueTypeRow: React.FC<{
  item: any;
  onEdit: (item: any) => void;
  onDelete: (item: any) => void;
  t: (key: string) => string;
}> = ({ item, onEdit, onDelete, t }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item._id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    background: isDragging ? "#f0f5ff" : "#fff",
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    marginBottom: 6,
    cursor: "default",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{ cursor: "grab", color: "#bfbfbf", fontSize: 16, flexShrink: 0 }}
      >
        <HolderOutlined />
      </span>
      <span style={{ flex: 1, fontWeight: 600 }}>{item.name}</span>
      {item.sectorId?.name && (
        <Tag color="blue" style={{ flexShrink: 0 }}>{item.sectorId.name}</Tag>
      )}
      <Button size="small" onClick={() => onEdit(item)}>{t("View")}</Button>
      <Button size="small" danger onClick={() => onDelete(item)}>{t("Delete")}</Button>
    </div>
  );
};

type Props = {};

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

const DailyRevenueSettings: React.FC<Props> = () => {
  const params = useParams();
  const { t } = useTranslation();
  const authUser: any = useAuth();
  const userData = useSelector((state: any) => state.userData.user);
  const workLocationsState = useSelector((state: any) => state.workLocations);
  const dispatch = useDispatch();
  const [clientOvertimes, setClientOvertimes] = useState<any>(undefined);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [locations, setLocations] = useState<any>(undefined);
  const [workSectors, setWorkSectors] = useState<any>(undefined);
  const [userWorkLocations, setUserWorkLocations] = useState<any[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>(() => {
    // Try to load from localStorage
    try {
      const saved = localStorage.getItem("dailyRevenue_locationFilter");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [modalDetails, setModalDetails] = useState<any>(undefined);
  const isAdminView = isAdmin(authUser.roles);

  const [revenueTypes, setRevenueTypes] = useState<any>(undefined);
  const [sortedRevenueTypes, setSortedRevenueTypes] = useState<any[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<any[]>([]);

  const dndSensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  }));

  const handleRevenueTypeDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSortedRevenueTypes((prev) => {
      const oldIndex = prev.findIndex((t: any) => t._id === active.id);
      const newIndex = prev.findIndex((t: any) => t._id === over.id);
      const next = arrayMove(prev, oldIndex, newIndex);
      axios
        .put(`${BASE_API_URL}/admin/${userData?.clientId}/reorderRevenueTypes`, {
          orderedIds: next.map((t: any) => t._id),
        })
        .catch((err) => console.error("Failed to save order:", err));
      return next;
    });
  };
  const [excludeExpenseMatrix, setExcludeExpenseMatrix] =
    useState<boolean>(false);
  const [showPaymentsDetails, setShowPaymentsDetails] =
    useState<boolean>(false);
  const [isCompactView, setIsCompactView] = useState<boolean>(false);
  const [dateSortOrder, setDateSortOrder] = useState<"asc" | "desc">("asc");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteConfirmValue, setDeleteConfirmValue] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState<any>(dayjs(new Date()));

  useEffect(() => {
    if (userData?.clientId) {
      // Load locations, work sectors, revenue types, and expense categories
      axios
        .get(`${BASE_API_URL}/${userData?.clientId}/locations`)
        .then((res) => {
          setLocations((res.data || []).filter((l: any) => !l.frozen));
        })
        .catch((err) => console.error(err));

      axios
        .get(`${BASE_API_URL}/admin/${userData.clientId}/getWorkSectors`)
        .then((res) => {
          setWorkSectors(res.data);
        })
        .catch((error) => console.error(error));

      axios
        .get(`${BASE_API_URL}/admin/${userData.clientId}/getRevenueTypes`)
        .then((res) => {
          setRevenueTypes(res.data);
          setSortedRevenueTypes(applyRevenueTypeOrder(res.data || []));
        })
        .catch((error) => console.error(error));

      axios
        .get(`${BASE_API_URL}/admin/expense-categories`, {
          params: { clientId: userData.clientId },
        })
        .then((res) => {
          setExpenseCategories(res.data?.categories || []);
        })
        .catch((err) => console.error(err));
    }
  }, [userData?.clientId]);

  // Load user work locations and auto-select them
  useEffect(() => {
    const loadUserWorkLocations = async () => {
      const userId = userData?._id || userData?.id;

      if (!userData?.accessToken || !userId) {
        return;
      }

      try {
        const workLocs = await getUserWorkLocationsHelper(
          userId,
          userData.accessToken,
          dispatch,
          workLocationsState,
        );
        setUserWorkLocations(workLocs);

        // Auto-select all assigned work locations
        // Only for non-admin users who have work locations assigned
        if (!isAdmin(authUser?.roles) && workLocs.length > 0) {
          const workLocationIds = workLocs.map((wl: any) => wl._id.toString());
          console.log("[DailyRevenue] Setting selectedLocations:", {
            workLocs,
            workLocationIds,
            isAdmin: isAdmin(authUser?.roles),
          });
          setSelectedLocations(workLocationIds);
        }
      } catch (error) {
        console.error("Error loading user work locations:", error);
      }
    };

    loadUserWorkLocations();
  }, [userData, authUser?.roles, dispatch, workLocationsState]);

  useEffect(() => {
    const handleResize = () => setIsCompactView(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Save location filter to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        "dailyRevenue_locationFilter",
        JSON.stringify(selectedLocations),
      );
    } catch (error) {
      console.error("Failed to save location filter:", error);
    }
  }, [selectedLocations]);

  const revenueTypeMap = React.useMemo(() => {
    if (!revenueTypes || !Array.isArray(revenueTypes)) return {};
    return Object.fromEntries(revenueTypes.map((rt: any) => [rt._id, rt.name]));
  }, [revenueTypes]);

  const matrixToggleStyle = React.useMemo(
    () => ({
      background: excludeExpenseMatrix ? "#fff7e6" : "#f0f5ff",
      border: `1px solid ${excludeExpenseMatrix ? "#ffd591" : "#d6e4ff"}`,
      borderRadius: 18,
      padding: "6px 10px",
      display: "flex",
      alignItems: "center",
      gap: 6,
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    }),
    [excludeExpenseMatrix],
  );

  const getExpenseMatrixSum = (record: any) =>
    (record.expenseMatrixEntries || []).reduce(
      (sum: number, item: any) => sum + Number(item.amount || 0),
      0,
    );

  const computePayable = (record: any) => {
    const cashAmount = Number(record.cashAmount) || 0;
    const cash = Number(record.cash) || 0;
    const delivery = Number(record.delivery) || 0;

    const paymentsArray = Array.isArray(record.paymentFromTheRevenue)
      ? record.paymentFromTheRevenue
      : [];

    const arrayPayments = paymentsArray
      .filter((p: any) => !p.other)
      .reduce(
        (sum: number, p: any) =>
          sum + (Number(p.total) || Number(p.amount) || Number(p.value) || 0),
        0,
      );

    const fallbackPayments =
      Number(
        record.paymentFromTheRevenueTotal ||
          record.paymentFromRevenueTotal ||
          0,
      ) || 0;

    const regularPayments =
      paymentsArray.length > 0 ? arrayPayments : fallbackPayments;

    const regularMatrixExpenses = (record.expenseMatrixEntries || [])
      .filter((item: any) => !item.other)
      .reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0);

    // Pazar za uplatu: Prihod gotovinom + Keš + Dostava - Plaćanje iz pazara (!other) - redovni rashodi (!other)
    return (
      cashAmount + cash + delivery - regularPayments - regularMatrixExpenses
    );
  };

  const refreshRevenues = () => {
    if (!userData?.clientId) return;
    return axios
      .get(
        `${BASE_API_URL}/admin/${userData.clientId}/getRevenue?date=${dayjs(
          selectedMonth,
        ).format("YYYY-MM-DD")}`,
      )
      .then((res) => {
        setClientOvertimes(res.data);
      })
      .catch((error) => console.error(error));
  };

  const handleDeleteRevenue = (row: any) => {
    setDeleteTarget(row);
    setDeleteConfirmValue("");
    setDeleteModalOpen(true);
  };

  const confirmDeleteRevenue = () => {
    const value = deleteConfirmValue.trim().toLowerCase();
    if (value !== "delete") {
      message.error(t('Please type "DELETE" to confirm'));
      return;
    }
    if (!deleteTarget?._id) return;
    setDeleteLoading(true);
    axios
      .delete(
        `${BASE_API_URL}/admin/${userData?.clientId}/deleteRevenue/${deleteTarget._id}`,
      )
      .then(() => {
        // instant UI feedback
        setClientOvertimes((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            revenues: (prev.revenues || []).filter(
              (rev: any) => rev._id !== deleteTarget._id,
            ),
          };
        });
        return refreshRevenues();
      })
      .finally(() => {
        setDeleteLoading(false);
        setDeleteModalOpen(false);
        setDeleteConfirmValue("");
        setDeleteTarget(null);
      });
  };

  const cancelDeleteRevenue = () => {
    setDeleteModalOpen(false);
    setDeleteConfirmValue("");
    setDeleteTarget(null);
  };

  const toggleCard = (id: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getRevenueTypeDiff = (record: any): number | null => {
    const items = Array.isArray(record.revenueByType) ? record.revenueByType : [];
    const validItems = items.filter(
      (item: any) => item.revenueTypeId && item.revenueTypeId._id,
    );
    if (validItems.length === 0) return null;
    const totalSales =
      (Number(record.cardAmount) || 0) +
      (Number(record.cashAmount) || 0) +
      (Number(record.cash) || 0) +
      (Number(record.delivery) || 0) +
      (Number(record.bankTransfer) || 0) +
      (Number(record.drugo) || 0);
    const revenueByTypeSum = validItems.reduce(
      (sum: number, item: any) => sum + Number(item.totalAmount || 0),
      0,
    );
    const diff = revenueByTypeSum - totalSales;
    return Math.abs(diff) >= 1 ? diff : null;
  };

  const isRevenueTypeMismatch = (record: any): boolean =>
    getRevenueTypeDiff(record) !== null;

  const columns: TableProps<DataType>["columns"] = [
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      width: 180,
      render: (value) => {
        const d = new Date(value);
        return (
          <Space size={8} align="center">
            <Tag color="geekblue" style={{ margin: 0 }}>
              {format(d, "EEE")}
            </Tag>
            <div style={{ lineHeight: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {format(d, "dd MMM")}
              </div>
              <div style={{ color: "#999", fontSize: 10 }}>
                {format(d, "yyyy")}
              </div>
            </div>
          </Space>
        );
      },
    },
    {
      title: t("Card Amount"),
      dataIndex: "cardAmount",
      key: "cardAmount",
      render: (value) =>
        isAdminView ? (
          <span>{value}</span>
        ) : (
          <span style={{ color: "#999" }}>••••</span>
        ),
    },
    {
      title: t("Cash Amount"),
      dataIndex: "cashAmount",
      key: "cashAmount",
      render: (value) =>
        isAdminView ? (
          <span>{value}</span>
        ) : (
          <span style={{ color: "#999" }}>••••</span>
        ),
    },
    {
      title: t("Cash"),
      dataIndex: "cash",
      key: "cash",
      render: (value) =>
        isAdminView ? (
          <span>{value}</span>
        ) : (
          <span style={{ color: "#999" }}>••••</span>
        ),
    },
    {
      title: t("Delivery"),
      dataIndex: "delivery",
      key: "delivery",
      render: (value) =>
        isAdminView ? (
          <span>{value}</span>
        ) : (
          <span style={{ color: "#999" }}>••••</span>
        ),
    },
    {
      title: t("Bank Transfer"),
      dataIndex: "bankTransfer",
      key: "bankTransfer",
      render: (value) =>
        isAdminView ? (
          <span>{value}</span>
        ) : (
          <span style={{ color: "#999" }}>••••</span>
        ),
    },
    {
      title: t("Ukupan pazar"),
      dataIndex: "totalSales",
      key: "totalSales",
      render: (_, record: any) => {
        const total =
          (Number(record.cardAmount) || 0) +
          (Number(record.cashAmount) || 0) +
          (Number(record.cash) || 0) +
          (Number(record.delivery) || 0) +
          (Number(record.bankTransfer) || 0) +
          (Number(record.drugo) || 0);

        return isAdminView ? (
          <b>{total.toLocaleString("sr-RS")}</b>
        ) : (
          <span style={{ color: "#999" }}>••••</span>
        );
      },
    },
    {
      title: t("Payments From Revenue"),
      dataIndex: "paymentFromTheRevenue",
      key: "paymentFromTheRevenue",
      render: (_, record: any) => {
        if (!isAdminView) {
          return <span style={{ color: "#999" }}>••••</span>;
        }

        const payments = record.paymentFromTheRevenue || [];
        const matrixEntries = record.expenseMatrixEntries || [];

        const categoryMap = new Map(
          expenseCategories.map((c: any) => [c._id?.toString(), c]),
        );

        const regular = payments.filter((p: any) => !p.other);
        const other = payments.filter((p: any) => p.other);
        const matrixRegular = matrixEntries.filter((m: any) => !m.other);
        const matrixOther = matrixEntries.filter((m: any) => m.other);

        const hasAny =
          payments.length > 0 ||
          (Array.isArray(matrixEntries) && matrixEntries.length > 0);

        if (!hasAny) {
          return <span style={{ color: "#999" }}>{t("No deductions")}</span>;
        }

        const regularSum = regular.reduce(
          (sum: number, item: any) => sum + Number(item.total || 0),
          0,
        );
        const otherSum = other.reduce(
          (sum: number, item: any) => sum + Number(item.total || 0),
          0,
        );
        const matrixRegularSum = matrixRegular.reduce(
          (sum: number, item: any) => sum + Number(item.amount || 0),
          0,
        );
        const matrixOtherSum = matrixOther.reduce(
          (sum: number, item: any) => sum + Number(item.amount || 0),
          0,
        );

        const popoverContent = (
          <div style={{ maxWidth: 320 }}>
            {regular.map((item: any) => (
              <div key={item._id}>
                <b>{item.description}</b>: -{item.total}
              </div>
            ))}
            {other.length > 0 && <Divider style={{ margin: "6px 0" }} />}
            {other.map((item: any) => (
              <div key={item._id} style={{ color: "#1677ff", fontWeight: 600 }}>
                <b>{item.description}</b>: -{item.total}
              </div>
            ))}
            {matrixRegular.length > 0 && (
              <>
                <Divider style={{ margin: "6px 0" }} />
                <div style={{ fontWeight: 600 }}>
                  {t("Operational Expenses")}
                </div>
                {matrixRegular.map((item: any, idx: number) => {
                  const category = categoryMap.get(
                    item.categoryId?._id?.toString() ||
                      item.categoryId?.toString(),
                  );
                  const supplier = category?.suppliers?.find(
                    (s: any) =>
                      s._id?.toString() ===
                      (item.supplierId?._id?.toString() ||
                        item.supplierId?.toString()),
                  );

                  return (
                    <div key={item._id || idx}>
                      <b>{category?.name || t("Unknown category")}</b> /{" "}
                      <span>{supplier?.name || t("Unknown supplier")}</span>: -
                      {item.amount}
                    </div>
                  );
                })}
              </>
            )}
            {matrixOther.length > 0 && (
              <>
                <Divider style={{ margin: "6px 0" }} />
                <div style={{ fontWeight: 600, color: "#722ed1" }}>
                  {t("Operational Expenses")} · {t("Other")}
                </div>
                {matrixOther.map((item: any, idx: number) => {
                  const category = categoryMap.get(
                    item.categoryId?._id?.toString() ||
                      item.categoryId?.toString(),
                  );
                  const supplier = category?.suppliers?.find(
                    (s: any) =>
                      s._id?.toString() ===
                      (item.supplierId?._id?.toString() ||
                        item.supplierId?.toString()),
                  );
                  return (
                    <div key={item._id || idx} style={{ color: "#722ed1" }}>
                      <b>{category?.name || t("Unknown category")}</b> /{" "}
                      <span>{supplier?.name || t("Unknown supplier")}</span>: -
                      {item.amount}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        );

        return (
          <>
            {showPaymentsDetails ? (
              <div style={{ lineHeight: 1.4, paddingTop: 4 }}>
                {popoverContent}
              </div>
            ) : (
              <Popover content={popoverContent} placement="left">
                <Space
                  direction="vertical"
                  size={4}
                  style={{ display: "flex" }}
                >
                  <Tag color="blue" style={{ width: 190 }}>
                    {t("Regular")} · {regular.length} · -{regularSum}
                  </Tag>
                  <Tag color="purple" style={{ width: 190 }}>
                    {t("Other")} · {other.length} · -{otherSum}
                  </Tag>
                  <Tag color="orange" style={{ width: 190 }}>
                    {t("Operational Expenses")} · {matrixRegular.length} · -
                    {matrixRegularSum}
                  </Tag>
                  {matrixOther.length > 0 && (
                    <Tag color="magenta" style={{ width: 190 }}>
                      {t("Operational Expenses")} · {t("Other")} ·{" "}
                      {matrixOther.length} · -{matrixOtherSum}
                    </Tag>
                  )}
                </Space>
              </Popover>
            )}
          </>
        );
      },
    },

    {
      title: t("Total Revenue"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (value) =>
        isAdminView ? (
          <b>{value?.toLocaleString("sr-RS")}</b>
        ) : (
          <span style={{ color: "#999" }}>••••</span>
        ),
    },
    {
      title: t("Pazar za uplatu"),
      key: "payableRevenue",
      render: (_: any, record: any) => {
        return isAdminView ? (
          <b>{computePayable(record).toLocaleString("sr-RS")}</b>
        ) : (
          <span style={{ color: "#999" }}>••••</span>
        );
      },
    },
    {
      title: t("Revenue By Type"),
      dataIndex: "revenueByType",
      key: "revenueByType",
      render: (revenueByType, record: any) => {
        if (!isAdminView) {
          return <span style={{ color: "#999" }}>••••</span>;
        }

        const items = Array.isArray(revenueByType) ? revenueByType : [];
        if (items.length === 0) {
          return <span style={{ color: "#999" }}>{t("No data")}</span>;
        }

        const total = items.reduce(
          (sum: number, item: any) => sum + Number(item.totalAmount || 0),
          0,
        );

        const popoverContent = (
          <div style={{ maxWidth: 320, lineHeight: 1.5 }}>
            {items.map((item: any) => (
              <div key={item._id}>
                <b>{item.revenueTypeId?.name || t("N/A")}</b>:{" "}
                {item.totalAmount}
              </div>
            ))}
          </div>
        );

        const diff = getRevenueTypeDiff(record);
        return showPaymentsDetails ? (
          <div style={{ lineHeight: 1.4, paddingTop: 4 }}>{popoverContent}</div>
        ) : (
          <Popover content={popoverContent} placement="left">
            <Space direction="vertical" size={4} style={{ display: "flex" }}>
              <Tag color="green" style={{ width: 190 }}>
                {t("Types")} · {items.length} · {total}
              </Tag>
              {diff !== null && (
                <Tag color="red" style={{ width: 190, fontWeight: 700 }}>
                  ⚠ {diff > 0 ? "+" : ""}{diff.toLocaleString("sr-RS")} RSD
                </Tag>
              )}
            </Space>
          </Popover>
        );
      },
    },
    {
      title: t("Location"),
      dataIndex: "locationId",
      key: "locationId",
      render: (value) => value?.name || "-",
    },
    {
      title: t("Notes"),
      dataIndex: "notes",
      key: "notes",
      render: (value) => value || "-",
    },
    {
      title: t("Action"),
      key: "action",
      width: 70,
      render: (row) => {
        if (!isAdminView) return null;
        return (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "view",
                  label: t("View"),
                  onClick: () => navigate(`/dailyRevenue/${row._id}`),
                },
                {
                  key: "delete",
                  label: <span style={{ color: "red" }}>{t("Delete")}</span>,
                  onClick: () => handleDeleteRevenue(row),
                },
              ],
            }}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  useEffect(() => {
    if (userData?.clientId && params.id !== "new") {
      axios
        .get(
          `${BASE_API_URL}/admin/${userData.clientId}/getRevenue?date=${dayjs(
            selectedMonth,
          ).format("YYYY-MM-DD")}`,
        )

        .then((res) => {
          setClientOvertimes(res.data);
        })
        .catch((error) => console.error(error));
    }
  }, [params.id, selectedMonth, userData?.clientId]);

  const handlePrevMonth = () => {
    setSelectedMonth(selectedMonth.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setSelectedMonth(selectedMonth.add(1, "month"));
  };

  const onChange = (key: string) => {
    console.log(key);
  };

  const revenueTypeColumns: TableProps<any>["columns"] = [
    {
      title: t("Revenue Type Name"),
      dataIndex: "name",
      key: "name",
      render: (value) => {
        return <b>{value}</b>;
      },
    },
    {
      title: t("Work Sector"),
      dataIndex: "sectorId",
      key: "workSector",
      render: (value) => {
        return <b>{value?.name}</b>;
      },
    },

    {
      title: t("Action"),
      key: "action",
      render: (row, record) => (
        <Space size="middle">
          <Button
            onClick={() => {
              setModalDetails(row);
              setModalOpen(true);
            }}
          >
            {t("View")}
          </Button>
          <Button
            danger
            onClick={() => {
              confirm({
                title: t("Are you sure you want to delete this revenue?"),
                content: t("This action cannot be undone."),
                okText: t("Yes, Delete"),
                okType: "danger",
                cancelText: t("Cancel"),
                onOk() {
                  return axios
                    .delete(
                      `${BASE_API_URL}/admin/${userData?.clientId}/deleteRevenueType/${row._id}`,
                    )
                    .then(() => {
                      return axios
                        .get(
                          `${BASE_API_URL}/admin/${
                            userData.clientId
                          }/getRevenue?date=${dayjs(selectedMonth).format(
                            "YYYY-MM-DD",
                          )}`,
                        )

                        .then((res) => {
                          setClientOvertimes(res.data);
                        });
                    })
                    .catch((error) => console.error(error));
                },
              });
            }}
          >
            {t("Delete")}
          </Button>
        </Space>
      ),
    },
  ];
  const filteredRevenues = clientOvertimes?.revenues?.filter((rev: any) => {
    const shouldInclude =
      selectedLocations.length > 0
        ? selectedLocations.includes(rev.locationId?._id)
        : true;

    // Debug logging
    if (selectedLocations.length > 0) {
      console.log("[DailyRevenue Filter]", {
        revLocationId: rev.locationId?._id,
        revLocationName: rev.locationId?.name,
        selectedLocations,
        shouldInclude,
      });
    }

    return shouldInclude;
  });

  filteredRevenues?.sort((a: any, b: any) => {
    const diff = new Date(a.date).getTime() - new Date(b.date).getTime();
    return dateSortOrder === "asc" ? diff : -diff;
  });

  const totalMatrixExpenses = filteredRevenues?.reduce(
    (sum: number, rev: any) => sum + getExpenseMatrixSum(rev),
    0,
  );

  const revenueTotals = React.useMemo(() => {
    if (!filteredRevenues || filteredRevenues.length === 0)
      return {
        totalPazar: 0,
        totalNakonOdbitaka: 0,
        adjustedAfterDeductions: 0,
        totalPayable: 0,
      };
    const totalPazar = filteredRevenues?.reduce((acc: number, cur: any) => {
      const total =
        (Number(cur.cardAmount) || 0) +
        (Number(cur.cashAmount) || 0) +
        (Number(cur.cash) || 0) +
        (Number(cur.delivery) || 0) +
        (Number(cur.bankTransfer) || 0) +
        (Number(cur.drugo) || 0);
      return acc + total;
    }, 0);

    const totalNakonOdbitaka = filteredRevenues?.reduce(
      (acc: number, cur: any) => acc + (Number(cur.totalAmount) || 0),
      0,
    );

    const adjustedAfterDeductions =
      (totalNakonOdbitaka || 0) -
      (excludeExpenseMatrix ? 0 : totalMatrixExpenses || 0);

    const totalPayable = filteredRevenues?.reduce(
      (acc: number, cur: any) => acc + computePayable(cur),
      0,
    );

    return {
      totalPazar,
      totalNakonOdbitaka,
      adjustedAfterDeductions,
      totalPayable,
    };
  }, [filteredRevenues, excludeExpenseMatrix, totalMatrixExpenses]);

  const renderTableSummary = () => (
    <>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={7}>
          <Text strong>{t("Ukupan pazar:")}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1}>
          {isAdminView && (
            <Text strong>
              {revenueTotals.totalPazar?.toLocaleString("sr-RS")} RSD
            </Text>
          )}
        </Table.Summary.Cell>
      </Table.Summary.Row>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={7}>
          <Text strong>{t("Nakon odbitaka:")}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1}>
          {isAdminView && (
            <Text strong>
              {revenueTotals.totalNakonOdbitaka?.toLocaleString("sr-RS")} RSD
            </Text>
          )}
        </Table.Summary.Cell>
      </Table.Summary.Row>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={7}>
          <Text strong>{t("Pazar za uplatu")}:</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1}>
          {isAdminView && (
            <Text strong>
              {revenueTotals.totalPayable?.toLocaleString("sr-RS")} RSD
            </Text>
          )}
        </Table.Summary.Cell>
      </Table.Summary.Row>
      {!excludeExpenseMatrix && (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={7}>
            <Text type="secondary">
              {t("Uključeni operativni troškovi iz matrice:")}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1}>
            {isAdminView && (
              <Text type="secondary">
                {totalMatrixExpenses?.toLocaleString("sr-RS")} RSD
              </Text>
            )}
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    </>
  );

  const renderRevenueCard = (record: any) => {
    const d = new Date(record.date);
    const totalSales =
      (Number(record.cardAmount) || 0) +
      (Number(record.cashAmount) || 0) +
      (Number(record.cash) || 0) +
      (Number(record.delivery) || 0) +
      (Number(record.bankTransfer) || 0) +
      (Number(record.drugo) || 0);
    const payments = record.paymentFromTheRevenue || [];
    const matrixEntries = record.expenseMatrixEntries || [];
    const regular = payments.filter((p: any) => !p.other);
    const other = payments.filter((p: any) => p.other);
    return (
      <div className="revenueCard" key={record._id}>
        <div className="revenueCard__header">
          <div>
            <div className="revenueCard__date">{format(d, "dd MMM yyyy")}</div>
            <div className="revenueCard__weekday">{format(d, "EEEE")}</div>
          </div>
          <Tag color="default" style={{ margin: 0 }}>
            {record.locationId?.name || t("No location")}
          </Tag>
        </div>
        <div className="revenueCard__row">
          <span>{t("Ukupan pazar")}</span>
          <strong>
            {isAdminView ? totalSales.toLocaleString("sr-RS") : "••••"}
          </strong>
        </div>
        <div className="revenueCard__row">
          <span>{t("Total Revenue")}</span>
          <strong>
            {isAdminView
              ? Number(record.totalAmount || 0).toLocaleString("sr-RS")
              : "••••"}
          </strong>
        </div>
        <div className="revenueCard__chips">
          {isAdminView ? (
            <>
              <Tag>
                {t("Card")} · {record.cardAmount || 0}
              </Tag>
              <Tag>
                {t("Cash")} · {record.cashAmount || 0}
              </Tag>
              <Tag>
                {t("Delivery")} · {record.delivery || 0}
              </Tag>
              <Tag>
                {t("Bank Transfer")} · {record.bankTransfer || 0}
              </Tag>
            </>
          ) : (
            <Tag color="default">••••</Tag>
          )}
        </div>
        <div className="revenueCard__meta">
          <Tag color="blue">
            {t("Regular")} · {regular.length}
          </Tag>
          <Tag color="purple">
            {t("Other")} · {other.length}
          </Tag>
          <Tag color="orange">
            {t("Operational Expenses")} · {matrixEntries.length}
          </Tag>
        </div>
        {isAdminView && (
          <div className="revenueCard__actions">
            <Button
              size="small"
              type="default"
              onClick={() => navigate(`/dailyRevenue/${record._id}`)}
            >
              {t("View")}
            </Button>
            <Button
              size="small"
              danger
              onClick={() => handleDeleteRevenue(record)}
            >
              {t("Delete")}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const items: TabsProps["items"] = [
    {
      key: "new",
      label: t("Pregled"),
      children: (
        <div className="drs-pregled">
          {/* Filters */}
          <div className="allDailyRevenueFilters">
            <div className="homeDatePicker">
              <div className="prev" onClick={handlePrevMonth}><LeftOutlined /></div>
              <div className="date">
                <DatePicker
                  picker="month"
                  className="modern-range-picker single-month-picker"
                  allowClear={false}
                  value={selectedMonth}
                  onChange={(date) => setSelectedMonth(date)}
                />
              </div>
              <div className="next" onClick={handleNextMonth}><RightOutlined /></div>
            </div>
            <div className="dailyRevenueFilters">
              <div className="dailyRevenueFilters__main">
                {(isAdmin(authUser.roles) || isModerator(authUser.roles)) && (
                  <Button type="primary" onClick={() => navigate("/dailyRevenue/new")}>
                    {t("Add Revenue")}
                  </Button>
                )}
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={t("Filter by locations")}
                  className="dailyRevenueFilters__select"
                  onChange={(values) => setSelectedLocations(values)}
                  value={selectedLocations}
                  disabled={userWorkLocations.length > 0 && !isAdmin(authUser?.roles)}
                  options={locations?.map((loc: any) => ({ label: loc.name, value: loc._id }))}
                />
                <Tooltip
                  title={
                    dateSortOrder === "asc"
                      ? t("Sorted oldest to newest — click to reverse")
                      : t("Sorted newest to oldest — click to reverse")
                  }
                >
                  <Button
                    icon={
                      dateSortOrder === "asc" ? (
                        <SortAscendingOutlined />
                      ) : (
                        <SortDescendingOutlined />
                      )
                    }
                    onClick={() =>
                      setDateSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                    }
                  >
                    {t("Date")}
                  </Button>
                </Tooltip>
                {selectedLocations.length > 0 &&
                  !(userWorkLocations.length > 0 && !isAdmin(authUser?.roles)) && (
                    <Tag
                      color="blue"
                      closable
                      onClose={() => setSelectedLocations([])}
                      style={{ padding: "4px 12px", fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <InfoCircleOutlined />
                      {selectedLocations.length === 1
                        ? t("1 location filtered")
                        : t("{{count}} locations filtered", { count: selectedLocations.length })}
                    </Tag>
                  )}
              </div>
              <div className="dailyRevenueFilters__toggles">
                <div className="matrixToggle" style={matrixToggleStyle}>
                  <Checkbox
                    checked={excludeExpenseMatrix}
                    onChange={(e) => setExcludeExpenseMatrix(e.target.checked)}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {excludeExpenseMatrix ? t("Excluded") : t("Included")}
                  </Text>
                  <Tooltip title={t("When checked, matrix costs stay out of the net total so you can view revenue without those deductions.")}>
                    <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>

          {/* Summary metric cards */}
          <div className="drs-summary">
            <div className="drs-metric-card drs-metric-card--pazar">
              <div className="drs-metric-card__label">{t("Ukupan pazar")}</div>
              <div className="drs-metric-card__value">
                {isAdminView ? revenueTotals.totalPazar.toLocaleString("sr-RS") : "••••"}
              </div>
              <div className="drs-metric-card__currency">RSD</div>
            </div>
            <div className="drs-metric-card drs-metric-card--prihod">
              <div className="drs-metric-card__label">{t("Nakon odbitaka")}</div>
              <div className="drs-metric-card__value">
                {isAdminView ? revenueTotals.totalNakonOdbitaka.toLocaleString("sr-RS") : "••••"}
              </div>
              <div className="drs-metric-card__currency">RSD</div>
            </div>
            <div className="drs-metric-card drs-metric-card--uplata">
              <div className="drs-metric-card__label">{t("Pazar za uplatu")}</div>
              <div className="drs-metric-card__value">
                {isAdminView ? revenueTotals.totalPayable.toLocaleString("sr-RS") : "••••"}
              </div>
              <div className="drs-metric-card__currency">RSD</div>
            </div>
            {!excludeExpenseMatrix && (
              <div className="drs-metric-card drs-metric-card--expenses">
                <div className="drs-metric-card__label">{t("Operativni troškovi")}</div>
                <div className="drs-metric-card__value">
                  {isAdminView ? (totalMatrixExpenses || 0).toLocaleString("sr-RS") : "••••"}
                </div>
                <div className="drs-metric-card__currency">RSD</div>
              </div>
            )}
            <div className="drs-metric-card drs-metric-card--count">
              <div className="drs-metric-card__label">{t("Broj unosa")}</div>
              <div className="drs-metric-card__value drs-metric-card__value--count">
                {filteredRevenues?.length ?? 0}
              </div>
              <div className="drs-metric-card__currency">{t("entries")}</div>
            </div>
          </div>

          {/* Day list */}
          <div className="drs-day-list">
            {(!filteredRevenues || filteredRevenues.length === 0) && (
              <div className="drs-empty">{t("No data for selected period.")}</div>
            )}
            {filteredRevenues?.map((record: any) => {
              const d = new Date(record.date);
              const totalSales =
                (Number(record.cardAmount) || 0) +
                (Number(record.cashAmount) || 0) +
                (Number(record.cash) || 0) +
                (Number(record.delivery) || 0) +
                (Number(record.bankTransfer) || 0) +
                (Number(record.drugo) || 0);
              const payable = computePayable(record);
              const payments = record.paymentFromTheRevenue || [];
              const matrixEntries = record.expenseMatrixEntries || [];
              const regular = payments.filter((p: any) => !p.other);
              const other = payments.filter((p: any) => p.other);
              const matrixRegular = matrixEntries.filter((m: any) => !m.other);
              const matrixOther = matrixEntries.filter((m: any) => m.other);
              const isExpanded = expandedCards.has(record._id);
              const cMap = new Map(expenseCategories.map((c: any) => [c._id?.toString(), c]));

              const mismatchDiff = getRevenueTypeDiff(record);
              return (
                <div className={`drs-day-card${mismatchDiff !== null ? " drs-day-card--mismatch" : ""}`} key={record._id}>
                  <div
                    className="drs-day-card__header"
                    onClick={() => toggleCard(record._id)}
                  >
                    <div className="drs-day-card__date-block">
                      <span className="drs-day-card__weekday">{format(d, "EEE")}</span>
                      <span className="drs-day-card__date">{format(d, "dd MMM")}</span>
                      <span className="drs-day-card__year">{format(d, "yyyy")}</span>
                    </div>
                    <Tag className="drs-day-card__location" style={{ margin: 0 }}>
                      {record.locationId?.name || t("No location")}
                    </Tag>
                    {mismatchDiff !== null && isAdminView && (
                      <Tooltip title={t("Razlika: grupe vs načini plaćanja")}>
                        <span className="drs-day-card__mismatch-badge">
                          {mismatchDiff > 0 ? "+" : ""}{mismatchDiff.toLocaleString("sr-RS")} RSD
                        </span>
                      </Tooltip>
                    )}
                    <div className="drs-day-card__quick">
                      <div className="drs-day-card__metric">
                        <span className="drs-metric-label">{t("Pazar")}</span>
                        <span className="drs-metric-value">
                          {isAdminView ? totalSales.toLocaleString("sr-RS") : "••••"}
                        </span>
                      </div>
                      <div className="drs-day-card__metric">
                        <span className="drs-metric-label">{t("Za uplatu")}</span>
                        <span className="drs-metric-value drs-metric-value--green">
                          {isAdminView ? payable.toLocaleString("sr-RS") : "••••"}
                        </span>
                      </div>
                    </div>
                    <div className="drs-day-card__expand-icon">
                      {isExpanded ? <UpOutlined /> : <DownOutlined />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="drs-day-card__body">
                      <div className="drs-day-card__breakdown">
                        {[
                          { label: t("Card Amount"), value: record.cardAmount },
                          { label: t("Cash Amount"), value: record.cashAmount },
                          { label: t("Cash"), value: record.cash },
                          { label: t("Delivery"), value: record.delivery },
                          { label: t("Bank Transfer"), value: record.bankTransfer },
                        ].map(({ label, value }) => (
                          <div key={label} className="drs-breakdown-item">
                            <span className="drs-breakdown-label">{label}</span>
                            <span className="drs-breakdown-value">
                              {isAdminView ? (Number(value) || 0).toLocaleString("sr-RS") : "••••"}
                            </span>
                          </div>
                        ))}
                        <div className="drs-breakdown-item drs-breakdown-item--pazar">
                          <span className="drs-breakdown-label">{t("Ukupan pazar")}</span>
                          <span className="drs-breakdown-value drs-breakdown-value--bold">
                            {isAdminView ? totalSales.toLocaleString("sr-RS") : "••••"}
                          </span>
                        </div>
                        <div className="drs-breakdown-item drs-breakdown-item--total">
                          <span className="drs-breakdown-label">{t("Nakon odbitaka")}</span>
                          <span className="drs-breakdown-value drs-breakdown-value--bold">
                            {isAdminView ? (Number(record.totalAmount) || 0).toLocaleString("sr-RS") : "••••"}
                          </span>
                        </div>
                        <div className="drs-breakdown-item drs-breakdown-item--uplata">
                          <span className="drs-breakdown-label">{t("Za uplatu")}</span>
                          <span className="drs-breakdown-value drs-breakdown-value--green">
                            {isAdminView ? payable.toLocaleString("sr-RS") : "••••"}
                          </span>
                        </div>
                      </div>

                      {isAdminView &&
                        (regular.length > 0 || other.length > 0 || matrixRegular.length > 0 || matrixOther.length > 0) && (
                          <div className="drs-day-card__deductions">
                            <div className="drs-section-title">{t("Deductions")}</div>
                            {regular.map((p: any) => (
                              <div key={p._id} className="drs-deduction-row">
                                <span>{p.description}</span>
                                <span className="drs-deduction-amount">
                                  -{(Number(p.total) || 0).toLocaleString("sr-RS")}
                                </span>
                              </div>
                            ))}
                            {other.map((p: any) => (
                              <div key={p._id} className="drs-deduction-row drs-deduction-row--other">
                                <span>{p.description}</span>
                                <span className="drs-deduction-amount">
                                  -{(Number(p.total) || 0).toLocaleString("sr-RS")}
                                </span>
                              </div>
                            ))}
                            {matrixRegular.map((item: any, idx: number) => {
                              const category = cMap.get(item.categoryId?._id?.toString() || item.categoryId?.toString());
                              const supplier = category?.suppliers?.find(
                                (s: any) => s._id?.toString() === (item.supplierId?._id?.toString() || item.supplierId?.toString())
                              );
                              return (
                                <div key={item._id || idx} className="drs-deduction-row drs-deduction-row--matrix">
                                  <span>{category?.name || t("Unknown")} / {supplier?.name || t("Unknown")}</span>
                                  <span className="drs-deduction-amount">
                                    -{(Number(item.amount) || 0).toLocaleString("sr-RS")}
                                  </span>
                                </div>
                              );
                            })}
                            {matrixOther.map((item: any, idx: number) => {
                              const category = cMap.get(item.categoryId?._id?.toString() || item.categoryId?.toString());
                              const supplier = category?.suppliers?.find(
                                (s: any) => s._id?.toString() === (item.supplierId?._id?.toString() || item.supplierId?.toString())
                              );
                              return (
                                <div key={item._id || idx} className="drs-deduction-row drs-deduction-row--matrix-other">
                                  <span>{category?.name || t("Unknown")} / {supplier?.name || t("Unknown")}</span>
                                  <span className="drs-deduction-amount">
                                    -{(Number(item.amount) || 0).toLocaleString("sr-RS")}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                      {isAdminView &&
                        Array.isArray(record.revenueByType) &&
                        record.revenueByType.length > 0 && (
                          <div className="drs-day-card__revenue-types">
                            <div className="drs-section-title">{t("Revenue By Type")}</div>
                            {record.revenueByType.map((item: any) => (
                              <div key={item._id} className="drs-deduction-row">
                                <span>{item.revenueTypeId?.name || t("N/A")}</span>
                                <span className="drs-deduction-amount drs-deduction-amount--green">
                                  {(Number(item.totalAmount) || 0).toLocaleString("sr-RS")}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                      {record.notes && (
                        <div className="drs-day-card__notes">
                          <span className="drs-notes-label">{t("Notes")}:</span> {record.notes}
                        </div>
                      )}

                      {isAdminView && (
                        <div className="drs-day-card__actions">
                          <Button
                            size="small"
                            onClick={(e) => { e.stopPropagation(); navigate(`/dailyRevenue/${record._id}`); }}
                          >
                            {t("View")}
                          </Button>
                          <Button
                            size="small"
                            danger
                            onClick={(e) => { e.stopPropagation(); handleDeleteRevenue(record); }}
                          >
                            {t("Delete")}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ),
    },
    {
      key: "1",
      label: t("Klasičan prikaz"),
      children: (
        <div className="dailyRevenuePage">
          <div className="allDailyRevenueFilters">
            <div className="homeDatePicker">
              <div className="prev" onClick={handlePrevMonth}>
                <LeftOutlined />
              </div>
              <div className="date">
                <DatePicker
                  picker="month"
                  className="modern-range-picker single-month-picker"
                  allowClear={false}
                  value={selectedMonth}
                  //   disabledDate={disabledDate}
                  onChange={(date) => {
                    setSelectedMonth(date);
                  }}
                />
              </div>
              <div className="next" onClick={handleNextMonth}>
                <RightOutlined />
              </div>
            </div>
            <div className="dailyRevenueFilters">
              <div className="dailyRevenueFilters__main">
                {(isAdmin(authUser.roles) || isModerator(authUser.roles)) && (
                  <Button
                    type="primary"
                    onClick={() => {
                      navigate("/dailyRevenue/new");
                    }}
                  >
                    {t("Add Revenue")}
                  </Button>
                )}
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={t("Filter by locations")}
                  className="dailyRevenueFilters__select"
                  onChange={(values) => setSelectedLocations(values)}
                  value={selectedLocations}
                  disabled={
                    userWorkLocations.length > 0 && !isAdmin(authUser?.roles)
                  }
                  options={locations?.map((loc: any) => ({
                    label: loc.name,
                    value: loc._id,
                  }))}
                />
                {selectedLocations.length > 0 &&
                  !(
                    userWorkLocations.length > 0 && !isAdmin(authUser?.roles)
                  ) && (
                    <Tag
                      color="blue"
                      closable
                      onClose={() => setSelectedLocations([])}
                      style={{
                        padding: "4px 12px",
                        fontSize: 13,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <InfoCircleOutlined />
                      {selectedLocations.length === 1
                        ? t("1 location filtered")
                        : t("{{count}} locations filtered", {
                            count: selectedLocations.length,
                          })}
                    </Tag>
                  )}
              </div>
              <div className="dailyRevenueFilters__toggles">
                <div className="matrixToggle" style={matrixToggleStyle}>
                  <Checkbox
                    checked={excludeExpenseMatrix}
                    onChange={(e) => setExcludeExpenseMatrix(e.target.checked)}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {excludeExpenseMatrix ? t("Excluded") : t("Included")}
                  </Text>
                  <Tooltip
                    title={t(
                      "When checked, matrix costs stay out of the net total so you can view revenue without those deductions.",
                    )}
                  >
                    <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                  </Tooltip>
                </div>
                <div className="detailsToggle">
                  <Checkbox
                    checked={showPaymentsDetails}
                    onChange={(e) => setShowPaymentsDetails(e.target.checked)}
                  />
                  <Tooltip
                    title={t(
                      "Show all deductions inline instead of hover pop-up.",
                    )}
                  >
                    <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                  </Tooltip>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {showPaymentsDetails ? t("Expanded") : t("Compact")}
                  </Text>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          {isCompactView ? (
            <>
              <div className="revenueCards">
                {filteredRevenues?.map((rev: any) => renderRevenueCard(rev))}
              </div>
              {revenueTotals && (
                <div className="revenueSummary">
                  <div>
                    <Text strong>{t("Ukupan pazar:")}</Text>
                    <div>
                      {isAdminView
                        ? `${revenueTotals.totalPazar.toLocaleString(
                            "sr-RS",
                          )} RSD`
                        : "••••"}
                    </div>
                  </div>
                  <div>
                    <Text strong>{t("Nakon odbitaka:")}</Text>
                    <div>
                      {isAdminView
                        ? `${revenueTotals.totalNakonOdbitaka.toLocaleString(
                            "sr-RS",
                          )} RSD`
                        : "••••"}
                    </div>
                  </div>
                  {!excludeExpenseMatrix && (
                    <div>
                      <Text type="secondary">
                        {t("Uključeni operativni troškovi iz matrice:")}
                      </Text>
                      <div>
                        {isAdminView
                          ? `${totalMatrixExpenses?.toLocaleString(
                              "sr-RS",
                            )} RSD`
                          : "••••"}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredRevenues}
              pagination={false}
              summary={() => renderTableSummary()}
              rowClassName={(record: any) =>
                isRevenueTypeMismatch(record) ? "drs-row--mismatch" : ""
              }
            />
          )}
        </div>
      ),
    },
    {
      key: "2",
      label: t("Revenue Types"),
      children: (
        <div style={{ maxWidth: 600 }}>
          {isAdmin(authUser.roles) && (
            <Button
              type="primary"
              style={{ marginBottom: 16 }}
              onClick={() => setModalOpen(true)}
            >
              {t("Add Revenue Type")}
            </Button>
          )}
          {sortedRevenueTypes.length > 0 && (
            <div style={{ marginBottom: 8, fontSize: 12, color: "#8c8c8c", display: "flex", alignItems: "center", gap: 6 }}>
              <HolderOutlined />
              {t("Drag rows to reorder — order is saved and applied in daily revenue form")}
            </div>
          )}
          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleRevenueTypeDragEnd}
          >
            <SortableContext
              items={sortedRevenueTypes.map((rt: any) => rt._id)}
              strategy={verticalListSortingStrategy}
            >
              {sortedRevenueTypes.map((item: any) => (
                <SortableRevenueTypeRow
                  key={item._id}
                  item={item}
                  t={t}
                  onEdit={(row) => {
                    setModalDetails(row);
                    setModalOpen(true);
                  }}
                  onDelete={(row) => {
                    confirm({
                      title: t("Are you sure you want to delete this revenue?"),
                      content: t("This action cannot be undone."),
                      okText: t("Yes, Delete"),
                      okType: "danger",
                      cancelText: t("Cancel"),
                      onOk() {
                        return axios
                          .delete(
                            `${BASE_API_URL}/admin/${userData?.clientId}/deleteRevenueType/${row._id}`,
                          )
                          .then(() =>
                            axios
                              .get(`${BASE_API_URL}/admin/${userData.clientId}/getRevenueTypes`)
                              .then((res) => {
                                setRevenueTypes(res.data);
                                setSortedRevenueTypes(applyRevenueTypeOrder(res.data || []));
                              }),
                          )
                          .catch((error) => console.error(error));
                      },
                    });
                  }}
                />
              ))}
            </SortableContext>
          </DndContext>
          {(!sortedRevenueTypes || sortedRevenueTypes.length === 0) && (
            <div style={{ color: "#9ca3af", textAlign: "center", padding: 32 }}>
              {t("No revenue types yet.")}
            </div>
          )}
        </div>
      ),
    },
  ];
  return (
    <>
      <Modal
        open={deleteModalOpen}
        title={t("Delete revenue?")}
        okText={t("Delete")}
        okButtonProps={{ danger: true, loading: deleteLoading }}
        cancelText={t("Cancel")}
        className="standardHeader standardFooter"
        onOk={confirmDeleteRevenue}
        onCancel={cancelDeleteRevenue}
        destroyOnClose
      >
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ color: "#4c5566" }}>
            {t("This action cannot be undone.")}
          </div>
          <Input
            placeholder={t('Type "DELETE" to confirm')}
            value={deleteConfirmValue}
            onChange={(e) => setDeleteConfirmValue(e.target.value)}
          />
        </div>
      </Modal>
      <Tabs defaultActiveKey="new" items={items} onChange={onChange} />

      <RevenueTypesModal
        isOpen={isModalOpen}
        modalData={modalDetails}
        onCloseModal={() => setModalOpen(false)}
        mode={modalDetails ? "edit" : "create"}
        onUpdate={() => {
          if (params.id !== "new" && userData.clientId) {
            axios
              .get(
                `${BASE_API_URL}/admin/${userData.clientId}/getRevenueTypes?date=${selectedMonth}`,
              )
              .then((res) => {
                setRevenueTypes(res.data);
                setSortedRevenueTypes(applyRevenueTypeOrder(res.data || []));
              })
              .catch((error) => console.error(error));
          }
        }}
        locations={locations}
        clientId={userData?.clientId}
        workSectors={workSectors}
      />
    </>
  );
};
export default DailyRevenueSettings;
